import { useState } from "react";
import { Sunrise, X, Frown, Annoyed, Meh, Smile, Laugh } from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/Btn";
import { moodColor, moodLevel } from "@/lib/config";
import { useCreateSession, usePrefs, useSavePrefs, useSessions, useUpdateSession } from "@/lib/store";

const FACES = [[-6, Frown], [-2, Annoyed], [0, Meh], [3, Smile], [6, Laugh]];
const today = () => new Date().toLocaleDateString("en-CA");

export function MorningCheck() {
  const { data: prefs, isFetched } = usePrefs();
  const { data: sessions = [] } = useSessions();
  const save = useSavePrefs();
  const create = useCreateSession();
  const update = useUpdateSession();
  const [logged, setLogged] = useState(null);
  const [note, setNote] = useState("");
  const hour = new Date().getHours();
  const doneToday = sessions.some((s) => s.path === "morning" && new Date(s.timestamp).toLocaleDateString("en-CA") === today());
  if (!isFetched || prefs?.morningCheck === false || hour < 5 || hour >= 11) return null;
  if (!logged && (doneToday || prefs?.morningDismissed === today())) return null;

  const log = async (v) => {
    try {
      setLogged(await create.mutateAsync({ path: "morning", moodOnly: true, moodNow: v, completed: true }));
    } catch {
      toast.error("Couldn't log your mood — please try again");
    }
  };
  const saveNote = async () => {
    try {
      await update.mutateAsync({ id: logged.id, patch: { moodNote: note.trim() || null } });
      toast.success("Morning note saved");
      setNote("");
      setLogged({ ...logged, noted: true });
    } catch {
      toast.error("Couldn't save your note — please try again");
    }
  };

  return (
    <div className="mt-8 rounded-[24px] bg-t-brand2 p-5 tf-rise" data-testid="morning-check">
      <div className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-2 font-bold text-t-onbrand2"><Sunrise size={18} /> Good morning — how are you waking up?</p>
        {!logged ? <button onClick={() => save.mutate({ morningDismissed: today() })} className="text-t-muted hover:text-t-on transition-colors" aria-label="Not today" data-testid="morning-dismiss"><X size={18} /></button> : null}
      </div>
      {!logged ? (
        <>
          <div className="mt-4 flex justify-between gap-2">
            {FACES.map(([v, Icon]) => (
              <button key={v} onClick={() => log(v)} disabled={create.isPending} data-testid={`morning-mood-${v}`}
                className="flex-1 flex flex-col items-center gap-1.5 rounded-2xl bg-t-surface py-3 border border-t-border hover:border-t-brand transition-[transform,border-color] duration-200 hover:-translate-y-0.5 disabled:opacity-50">
                <Icon size={30} style={{ color: moodColor(v) }} />
                <span className="text-[12px] font-semibold text-t-on2">{moodLevel(v).label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => save.mutate({ morningCheck: false })} className="mt-3 text-xs text-t-muted hover:text-t-brand transition-colors" data-testid="morning-turn-off">Don't show the morning check</button>
        </>
      ) : (
        <div className="mt-3" data-testid="morning-logged">
          <p className="text-t-on">Logged: <b>{moodLevel(logged.moodNow)?.label}</b>. Thank you for checking in with yourself — have a gentle day.</p>
          {!logged.noted ? (
            <div className="mt-3 flex gap-2">
              <input className="tf-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Add a note (optional) — slept badly, feeling hopeful…" data-testid="morning-note-input" />
              <Btn size="md" label="Save" disabled={!note.trim()} loading={update.isPending} onClick={saveNote} testId="morning-note-save" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
